import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Shield, Scale } from 'lucide-react';

export default function Terms() {
  return (
    <main className="min-h-screen bg-gradient-to-br from-[#102318] via-[#183326] to-[#0b1710] text-white pt-20 px-4 pb-16 font-[Cormorant_Garamond]">
      <section className="max-w-5xl mx-auto">
        <p className="text-[#ffcc32] font-black uppercase text-sm tracking-widest">Legal</p>
        <h1 className="text-5xl md:text-7xl uppercase mt-2 mb-8" style={{ fontFamily: 'Cinzel, serif', letterSpacing: "0.1em" }}>
          Terms & Conditions
        </h1>
        
        <div className="bg-black/30 border border-white/10 rounded-lg p-8 mb-8">
          <p className="text-zinc-400 leading-relaxed">
            Welcome to RRide Moto Studio. By using our website and services, you agree to comply with and be bound by the following Terms & Conditions. Please read them carefully.
          </p>
        </div>

        <div className="space-y-8 text-zinc-300">
          <div className="bg-black/20 border border-white/5 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="text-[#ffcc32]" size={24} />
              <h2 className="text-2xl font-bold text-white uppercase">1. Accurate Information</h2>
            </div>
            <p className="leading-relaxed">
              Customers must provide accurate, complete, and current information while placing orders. We reserve the right to cancel orders if the provided information is found to be incorrect, incomplete, or fraudulent.
            </p>
          </div>

          <div className="bg-black/20 border border-white/5 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <Shield className="text-[#ffcc32]" size={24} />
              <h2 className="text-2xl font-bold text-white uppercase">2. Order Cancellation</h2>
            </div>
            <p className="leading-relaxed">
              RRide Moto Studio reserves the right to cancel suspicious or fraudulent orders at any time without prior notice. In such cases, refunds will be processed to the original payment method.
            </p>
          </div>

          <div className="bg-black/20 border border-white/5 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <Scale className="text-[#ffcc32]" size={24} />
              <h2 className="text-2xl font-bold text-white uppercase">3. Product Availability & Pricing</h2>
            </div>
            <p className="leading-relaxed">
              Product availability may change without prior notice. We strive to maintain accurate inventory, but errors may occur. Prices are subject to change without notice. We reserve the right to modify or discontinue products at any time.
            </p>
          </div>

          <div className="bg-black/20 border border-white/5 rounded-lg p-6">
            <h2 className="text-2xl font-bold text-white uppercase mb-4">4. Warranty Policy</h2>
            <p className="leading-relaxed">
              Warranty is available only on eligible products where specifically mentioned. Warranty terms vary by product and manufacturer. Please check individual product pages for warranty information. We do not provide warranty on products unless explicitly stated.
            </p>
          </div>

          <div className="bg-black/20 border border-white/5 rounded-lg p-6">
            <h2 className="text-2xl font-bold text-white uppercase mb-4">5. Installation & Usage</h2>
            <p className="leading-relaxed">
              Installation of motorcycle accessories is the customer's responsibility. RRide Moto Studio is not responsible for damage caused by improper installation, misuse, or unauthorized modifications of products. We recommend professional installation for complex products.
            </p>
          </div>

          <div className="bg-black/20 border border-white/5 rounded-lg p-6">
            <h2 className="text-2xl font-bold text-white uppercase mb-4">6. Payment Security</h2>
            <p className="leading-relaxed">
              All payments are processed securely through Razorpay. We do not store or have access to your complete payment information. By using our website, you agree to Razorpay's terms and conditions.
            </p>
          </div>

          <div className="bg-black/20 border border-white/5 rounded-lg p-6">
            <h2 className="text-2xl font-bold text-white uppercase mb-4">7. Shipping & Delivery</h2>
            <p className="leading-relaxed">
              Shipping times are estimates and not guaranteed. We are not responsible for delays caused by courier companies, weather conditions, strikes, or other unforeseen circumstances. Please refer to our <Link to="/shipping" className="text-[#ffcc32] hover:underline">Shipping & Delivery Policy</Link> for detailed information.
            </p>
          </div>

          <div className="bg-black/20 border border-white/5 rounded-lg p-6">
            <h2 className="text-2xl font-bold text-white uppercase mb-4">8. Returns & Refunds</h2>
            <p className="leading-relaxed">
              Our refund and cancellation policy is outlined separately. Please review our <Link to="/refund" className="text-[#ffcc32] hover:underline">Refund & Cancellation Policy</Link> for complete details about order cancellations and refund processing.
            </p>
          </div>

          <div className="bg-black/20 border border-white/5 rounded-lg p-6">
            <h2 className="text-2xl font-bold text-white uppercase mb-4">9. Privacy & Data</h2>
            <p className="leading-relaxed">
              Your privacy is important to us. Please review our <Link to="/privacy" className="text-[#ffcc32] hover:underline">Privacy Policy</Link> to understand how we collect, use, and protect your personal information.
            </p>
          </div>

          <div className="bg-black/20 border border-white/5 rounded-lg p-6">
            <h2 className="text-2xl font-bold text-white uppercase mb-4">10. Governing Law & Jurisdiction</h2>
            <p className="leading-relaxed">
              All disputes arising from the use of this website or purchase of products shall be subject to the jurisdiction of courts in Bhiwandi, Maharashtra, India. These terms shall be governed by and construed in accordance with the laws of India.
            </p>
          </div>

          <div className="bg-black/20 border border-white/5 rounded-lg p-6">
            <h2 className="text-2xl font-bold text-white uppercase mb-4">11. Modifications</h2>
            <p className="leading-relaxed">
              RRide Moto Studio reserves the right to modify these Terms & Conditions at any time without prior notice. Continued use of the website after changes constitutes acceptance of the modified terms.
            </p>
          </div>
        </div>

        <div className="mt-12 bg-gradient-to-r from-[#ffcc32]/10 to-transparent border border-[#ffcc32]/20 rounded-lg p-6">
          <h3 className="text-xl font-bold text-white uppercase mb-3">Need Help?</h3>
          <p className="text-zinc-400 mb-4">
            If you have any questions about these Terms & Conditions, please contact us:
          </p>
          <div className="space-y-2 text-sm">
            <p><span className="text-[#ffcc32]">Email:</span> rridemods@gmail.com</p>
            <p><span className="text-[#ffcc32]">Phone:</span> +91 8793678780</p>
            <p><span className="text-[#ffcc32]">Address:</span> Ground Floor, Mahaveer Building, Mandai - Dhamankar Naka Road, Near Payal Talkies, Narpoli, Bhiwandi, Maharashtra 421305, India</p>
          </div>
        </div>

        <div className="mt-8 text-center text-zinc-500 text-sm">
          <p>Last Updated: July 2026</p>
        </div>
      </section>
    </main>
  );
}
